/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LegendItem } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendItem';
import { SortOptions } from 'src/luis/projects/analytics/types/SortOptions';

export type LegendProps = {
    /**
     * Current selected sorting type.
     */
    sortType: SortOptions;

    /**
     * Setter to change the currently selected sort type.
     */
    setSortType: (sortType: SortOptions) => void;
};

/**
 * Enum with the possible options for legend items in the distribution chart
 */
enum LegendType {
    correct = 'correct',
    incorrect = 'incorrect',
    unclear = 'unclear'
}

const messages = defineMessages({
    correctLegend: {
        id: 'analytics.predictionsPerIntentCard.correctLegend',
        defaultMessage: 'Correctly predicted'
    },
    unclearLegend: {
        id: 'analytics.predictionsPerIntentCard.unclearLegend',
        defaultMessage: 'Unclear'
    },
    incorrectLegend: {
        id: 'analytics.predictionsPerIntentCard.incorrectLegend',
        defaultMessage: 'Incorrectly predicted'
    }
});

/**
 * The Legend component of the distribution chart.
 *
 * @param props The properties of the legend.
 */
export const Legend = (props: LegendProps) => {
    const { sortType, setSortType } = props;
    const { localize } = useLocalization();

    const legendClicked = (clickedSortType: SortOptions) => {
        if (sortType !== clickedSortType) {
            setSortType(clickedSortType);

            return;
        }
        setSortType(SortOptions.none);
    };

    return (
        <FocusZone>
            <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
                <Stack verticalAlign="center" horizontal>
                    <LegendItem
                        color="correct"
                        onClick={() => legendClicked(SortOptions.correct)}
                        isDimmed={isLegendDimmed(sortType, LegendType.correct)}
                    />
                    <Text>{localize(messages.correctLegend)}</Text>
                </Stack>
                <Stack verticalAlign="center" horizontal>
                    <LegendItem
                        color="unclear"
                        onClick={() => legendClicked(SortOptions.unclear)}
                        isDimmed={isLegendDimmed(sortType, LegendType.unclear)}
                    />
                    <Text>{localize(messages.unclearLegend)}</Text>
                </Stack>
                <Stack verticalAlign="center" horizontal>
                    <LegendItem
                        color="incorrect"
                        onClick={() => legendClicked(SortOptions.incorrect)}
                        isDimmed={isLegendDimmed(sortType, LegendType.incorrect)}
                    />
                    <Text>{localize(messages.incorrectLegend)}</Text>
                </Stack>
            </Stack>
        </FocusZone>
    );
};

/**
 * Decides if the given legend type should be dimmed or not.
 *
 * @param sortType Current selected sorting type.
 * @param legendType Type of Legend item.
 */
const isLegendDimmed = (sortType: string, legendType: string): boolean =>
    Object.values(LegendType).indexOf(sortType as any) !== -1 && sortType !== legendType;
